import { grossFromNet, netFromGross, productSchema, VAT_RATES_BP, type ProductInput, type ShopifyPriceStatus } from "@erp/shared";
import { useForm } from "@tanstack/react-form";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Field, FieldGroup } from "~/components/ui/field";
import { Input, Textarea } from "~/components/ui/input";
import { MoneyInput } from "~/components/ui/money-input";
import { Select } from "~/components/ui/select";
import { Sheet } from "~/components/ui/sheet";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { errorAt, listQuery, schemaErrors, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { HelpTip } from "~/components/ui/help-tip";
import { useMarginRate } from "~/lib/margin";

export const productQuery = (id: string) =>
  queryOptions({ queryKey: ["products", id], queryFn: () => unwrap(api.products({ id }).get()) });

export type Product = Awaited<ReturnType<NonNullable<ReturnType<typeof productQuery>["queryFn"]>>>;
export type ProductVariant = Product["variants"][number];

export const suppliersForSelectQuery = queryOptions({
  queryKey: ["suppliers", "list", "select"],
  queryFn: () => unwrap(api.suppliers.get({ query: listQuery({ limit: 500, sort: "name" }) })),
  staleTime: 60_000,
});

interface VariantDraft {
  id?: string;
  sku: string;
  ean: string;
  values: string[];
  weightGrams: string;
  salePriceCents: number | null;
  costCents: number | null;
  /** The article has stock movements: its cost is the weighted average of the ledger, changed by a revaluation. */
  costFromLedger: boolean;
  /** Cost as loaded, to tell a deliberate revaluation from a card simply saved again. */
  loadedCostCents: number | null;
  /** Sold on the website: a price changed here is sent to the Shopify variant. */
  shopifyLinked: boolean;
  priceStatus: ShopifyPriceStatus | null;
  priceError: string | null;
  reorderPoint: string;
  targetStock: string;
  mainSupplierId: string;
}

interface Draft {
  title: string;
  productType: string;
  description: string;
  supplierLabel: string;
  vatRateBp: string;
  hsCode: string;
  originCountry: string;
  axes: string[];
  variants: VariantDraft[];
}

const MAX_AXES = 3;
const NO_SUPPLIER = "none";

function emptyVariant(axes: number): VariantDraft {
  return {
    sku: "",
    ean: "",
    values: Array.from({ length: axes }, () => ""),
    weightGrams: "",
    salePriceCents: null,
    costCents: null,
    costFromLedger: false,
    loadedCostCents: null,
    shopifyLinked: false,
    priceStatus: null,
    priceError: null,
    reorderPoint: "0",
    targetStock: "0",
    mainSupplierId: NO_SUPPLIER,
  };
}

function toDraft(product?: Product): Draft {
  if (!product)
    return {
      title: "",
      productType: "",
      description: "",
      supplierLabel: "",
      vatRateBp: "2000",
      hsCode: "",
      originCountry: "",
      axes: [],
      variants: [emptyVariant(0)],
    };
  const axes = product.variants[0]?.options.map((o) => o.name) ?? [];
  return {
    title: product.title,
    productType: product.productType ?? "",
    description: product.description ?? "",
    supplierLabel: product.supplierLabel ?? "",
    vatRateBp: String(product.vatRateBp),
    hsCode: product.hsCode ?? "",
    originCountry: product.originCountry ?? "",
    axes,
    variants: product.variants.map((v) => ({
      id: v.id,
      sku: v.sku,
      ean: v.ean ?? "",
      values: axes.map((axis) => v.options.find((o) => o.name === axis)?.value ?? ""),
      weightGrams: v.weightGrams != null ? String(v.weightGrams) : "",
      salePriceCents: v.salePriceCents,
      costCents: v.costCents,
      costFromLedger: v.costFromLedger,
      loadedCostCents: v.costCents,
      shopifyLinked: Boolean(v.shopifyVariantId),
      priceStatus: v.shopifyPriceStatus,
      priceError: v.shopifyPriceError,
      reorderPoint: String(v.reorderPoint),
      targetStock: String(v.targetStock),
      mainSupplierId: v.mainSupplierId ?? NO_SUPPLIER,
    })),
  };
}

/**
 * The cost of an existing variant is its weighted average cost, recomputed by every receipt: it is
 * sent only when the user changed it here, so a form opened before a receipt never overwrites it.
 */
function toPayload(d: Draft): ProductInput {
  return {
    title: d.title,
    productType: d.productType,
    description: d.description,
    supplierLabel: d.supplierLabel,
    vatRateBp: Number(d.vatRateBp),
    hsCode: d.hsCode,
    originCountry: d.originCountry,
    variants: d.variants.map((v) => ({
      id: v.id,
      sku: v.sku,
      ean: v.ean,
      options: d.axes.map((name, i) => ({ name, value: v.values[i] ?? "" })).filter((o) => o.name && o.value),
      weightGrams: v.weightGrams.trim() === "" ? null : v.weightGrams,
      salePriceCents: v.salePriceCents ?? Number.NaN,
      // A stocked article's cost is not the card's to overwrite (it may predate a receipt): revalued apart.
      costCents: v.costFromLedger ? undefined : (v.costCents ?? undefined),
      reorderPoint: v.reorderPoint,
      targetStock: v.targetStock,
      mainSupplierId: v.mainSupplierId === NO_SUPPLIER ? null : v.mainSupplierId,
    })),
  };
}

const FORM_ID = "product-form";

export function ProductFormSheet({
  open,
  onOpenChange,
  product,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: Product;
  onSaved?: (p: Product) => void;
}) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={product ? product.title : t("products.new")}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID} loading={saving}>
            {product ? t("common.actions.save") : t("common.actions.create")}
          </Button>
        </>
      }
    >
      {open ? (
        <ProductForm
          product={product}
          onSavingChange={setSaving}
          onDone={(saved) => {
            onOpenChange(false);
            onSaved?.(saved);
          }}
        />
      ) : null}
    </Sheet>
  );
}

function ProductForm({
  product,
  onDone,
  onSavingChange,
}: {
  product?: Product;
  onDone: (p: Product) => void;
  onSavingChange: (saving: boolean) => void;
}) {
  const t = useT();
  const format = useFormat();
  const margin = useMarginRate();
  const queryClient = useQueryClient();
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});
  const suppliers = useQuery(suppliersForSelectQuery);
  /** Web products mirror Shopify: editing their web fields here would be overwritten by the next sync. */
  const isWeb = Boolean(product?.shopifyProductId);
  const families = useQuery({ queryKey: ["products", "types"], queryFn: () => unwrap(api.products.types.get()) });

  const capabilities = useQuery({
    queryKey: ["integrations", "capabilities"],
    queryFn: () => unwrap(api.integrations.capabilities.get()),
  });
  /** Where a web price stands: waiting (and why), sent, or refused by Shopify. */
  const priceHint = (v: VariantDraft) =>
    v.priceStatus === "synced"
      ? t("products.shopifyPrice.synced")
      : v.priceStatus === "failed"
        ? t("products.shopifyPrice.failed", { error: v.priceError ?? "" })
        : v.priceStatus === "pending"
          ? t(capabilities.data?.shopifyPricePush ? "products.shopifyPrice.pending" : "products.shopifyPrice.pendingOff")
          : t("products.priceFromShopify");

  const can = useCan();
  const seeCosts = can("costs:read");
  const canRevalue = seeCosts && can("stock:write");
  const save = useMutation({
    mutationFn: async (draft: Draft) => {
      const data = productSchema.parse(toPayload(draft));
      if (!product) return unwrap(api.products.post(data));
      const saved = await unwrap(api.products({ id: product.id }).put(data));
      // A cost changed on a stocked article is a revaluation, written to the stock ledger.
      const revalued = draft.variants.filter(
        (v) => v.id && v.costFromLedger && v.costCents !== null && v.costCents !== v.loadedCostCents,
      );
      for (const v of revalued) {
        await unwrap(api.products.variants({ id: v.id! }).cost.post({ costCents: v.costCents!, note: null }));
      }
      return revalued.length ? unwrap(api.products({ id: product.id }).get()) : saved;
    },
    onMutate: () => onSavingChange(true),
    onSettled: () => onSavingChange(false),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      toast.success(product ? t("products.updated") : t("products.created"));
      onDone(saved);
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const form = useForm({
    defaultValues: toDraft(product),
    onSubmit: async ({ value }) => {
      setSubmitted(true);
      if (Object.keys(schemaErrors(productSchema, toPayload(value))).length > 0) return;
      await save.mutateAsync(value).catch(() => undefined);
    },
  });

  const supplierOptions = [
    { value: NO_SUPPLIER, label: t("common.states.none") },
    ...(suppliers.data?.items ?? []).map((s) => ({ value: s.id, label: s.name })),
  ];
  const vatOptions = VAT_RATES_BP.map((bp) => ({ value: String(bp), label: format.percent(bp / 10_000) }));

  return (
    <form
      id={FORM_ID}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.Subscribe selector={(s) => s.values}>
        {(values) => {
          const errors: ErrorMap = {
            ...serverErrors,
            ...(submitted ? schemaErrors(productSchema, toPayload(values)) : {}),
          };
          const err = (path: string) => errorAt(t, errors, path);
          const vat = Number(values.vatRateBp);
          const setVariant = (index: number, patch: Partial<VariantDraft>) =>
            form.setFieldValue(
              "variants",
              values.variants.map((v, i) => (i === index ? { ...v, ...patch } : v)),
            );
          const setAxes = (axes: string[]) => {
            form.setFieldValue("axes", axes);
            form.setFieldValue(
              "variants",
              values.variants.map((v) => ({ ...v, values: axes.map((_, i) => v.values[i] ?? "") })),
            );
          };

          return (
            <div className="flex flex-col gap-7">
              {isWeb ? (
                <p className="flex gap-2.5 rounded-[10px] bg-info-soft px-3.5 py-3 text-sm text-info">
                  <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {t("products.webLocked")}
                  <HelpTip id="product.webLocked" className="-my-1 ml-auto" />
                </p>
              ) : null}

              <FieldGroup>
                <form.Field name="title">
                  {(field) => (
                    <Field label={t("products.fields.title")} error={err("title")}>
                      <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} disabled={isWeb} />
                    </Field>
                  )}
                </form.Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <form.Field name="productType">
                    {(field) => (
                      <Field
                        label={t("products.fields.productType")}
                        optionalLabel={t("common.states.optional")}
                        hint={isWeb ? undefined : t("families.datalistHint")}
                      >
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          disabled={isWeb}
                          list="product-families"
                        />
                      </Field>
                    )}
                  </form.Field>
                  <datalist id="product-families">
                    {(families.data ?? []).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </datalist>
                  <form.Field name="vatRateBp">
                    {(field) => (
                      <Field label={t("products.fields.vatRate")}>
                        <Select value={field.state.value} onValueChange={field.handleChange} options={vatOptions} />
                      </Field>
                    )}
                  </form.Field>
                  {/* Customs (CN23 of a parcel leaving the EU): the ERP's, even on a web product. */}
                  <form.Field name="hsCode">
                    {(field) => (
                      <Field
                        label={t("products.fields.hsCode")}
                        optionalLabel={t("common.states.optional")}
                        hint={t("products.fields.hsCodeHint")}
                        error={err("hsCode")}
                      >
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          inputMode="numeric"
                        />
                      </Field>
                    )}
                  </form.Field>
                  <form.Field name="originCountry">
                    {(field) => (
                      <Field
                        label={t("products.fields.originCountry")}
                        optionalLabel={t("common.states.optional")}
                        hint={t("products.fields.originCountryHint")}
                        error={err("originCountry")}
                      >
                        <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} maxLength={2} />
                      </Field>
                    )}
                  </form.Field>
                </div>
                {/* The name suppliers read on purchase orders: the ERP's, even on a web product. */}
                <form.Field name="supplierLabel">
                  {(field) => (
                    <Field
                      label={t("products.fields.supplierLabel")}
                      optionalLabel={t("common.states.optional")}
                      hint={t("products.fields.supplierLabelHint")}
                      error={err("supplierLabel")}
                    >
                      <Input value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} maxLength={200} />
                    </Field>
                  )}
                </form.Field>
                {isWeb ? null : (
                  <form.Field name="description">
                    {(field) => (
                      <Field label={t("products.fields.description")} optionalLabel={t("common.states.optional")}>
                        <Textarea value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} rows={3} />
                      </Field>
                    )}
                  </form.Field>
                )}
              </FieldGroup>

              {isWeb ? null : (
                <FieldGroup title={t("products.fields.options")}>
                  <p className="-mt-2 text-sm text-fg-3">{t("products.axesHint")}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {values.axes.map((axis, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <Input
                          value={axis}
                          onChange={(e) => setAxes(values.axes.map((a, i) => (i === index ? e.target.value : a)))}
                          aria-label={t("products.fields.options")}
                          className="w-36"
                        />
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setAxes(values.axes.filter((_, i) => i !== index))}
                          aria-label={t("common.actions.remove")}
                        >
                          <X />
                        </Button>
                      </div>
                    ))}
                    {values.axes.length < MAX_AXES ? (
                      <Button variant="secondary" size="sm" onClick={() => setAxes([...values.axes, ""])}>
                        <Plus />
                        {t("products.addAxis")}
                      </Button>
                    ) : null}
                  </div>
                </FieldGroup>
              )}

              <FieldGroup title={t("products.fields.variants")}>
                {errors.variants ? <p className="text-xs font-medium text-danger">{err("variants")}</p> : null}
                <div className="flex flex-col gap-3">
                  {values.variants.map((v, index) => {
                    const path = `variants.${index}`;
                    const optionLabel = values.axes
                      .map((_, i) => v.values[i])
                      .filter(Boolean)
                      .join(" / ");
                    return (
                      <div key={v.id ?? `new-${index}`} className="rounded-panel border border-line p-4">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-medium">{optionLabel || v.sku || t("products.singular")}</p>
                          {!isWeb && values.variants.length > 1 ? (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() =>
                                form.setFieldValue(
                                  "variants",
                                  values.variants.filter((_, i) => i !== index),
                                )
                              }
                              aria-label={t("common.actions.remove")}
                            >
                              <Trash2 />
                            </Button>
                          ) : null}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-4">
                          {isWeb
                            ? null
                            : values.axes.map((axis, i) => (
                                <Field key={i} label={axis || t("products.fields.options")} className="sm:col-span-2">
                                  <Input
                                    value={v.values[i] ?? ""}
                                    onChange={(e) =>
                                      setVariant(index, { values: v.values.map((x, j) => (j === i ? e.target.value : x)) })
                                    }
                                  />
                                </Field>
                              ))}
                          <Field label={t("products.fields.sku")} error={err(`${path}.sku`)} className="sm:col-span-2">
                            <Input
                              value={v.sku}
                              onChange={(e) => setVariant(index, { sku: e.target.value.toUpperCase() })}
                              className="uppercase tabular"
                            />
                          </Field>
                          <Field
                            label={t("products.fields.ean")}
                            optionalLabel={t("common.states.optional")}
                            className="sm:col-span-2"
                          >
                            <Input
                              value={v.ean}
                              inputMode="numeric"
                              onChange={(e) => setVariant(index, { ean: e.target.value })}
                              className="tabular"
                            />
                          </Field>
                          <Field label={t("products.fields.salePrice")} error={err(`${path}.salePriceCents`)}>
                            <MoneyInput
                              value={v.salePriceCents}
                              onChange={(cents) => setVariant(index, { salePriceCents: cents })}
                              suffix="€"
                            />
                          </Field>
                          {/* Prices are often known TTC (the web shop's): typed here, the HT follows at the product's VAT. */}
                          <Field label={t("products.fields.salePriceTtc")} hint={v.shopifyLinked ? priceHint(v) : undefined}>
                            <MoneyInput
                              value={v.salePriceCents === null ? null : grossFromNet(v.salePriceCents, vat)}
                              onChange={(cents) =>
                                setVariant(index, { salePriceCents: cents === null ? null : netFromGross(cents, vat) })
                              }
                              suffix="€"
                            />
                          </Field>
                          {/* Costs and margins are hidden from the roles that do not see them (guide: roles). */}
                          {seeCosts ? (
                            <Field
                              label={t("products.fields.costPrice")}
                              hint={[
                                v.salePriceCents && v.costCents !== null
                                  ? t("products.marginHint", {
                                      label: margin.label,
                                      rate: format.percent(margin.rate(v.salePriceCents - v.costCents, v.costCents) ?? 0),
                                    })
                                  : null,
                                v.costFromLedger ? t(canRevalue ? "products.costRevalued" : "products.costFromLedger") : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                              className="sm:col-span-2"
                            >
                              <MoneyInput
                                value={v.costCents}
                                onChange={(cents) => setVariant(index, { costCents: cents })}
                                suffix="€"
                                disabled={v.costFromLedger && !canRevalue}
                              />
                            </Field>
                          ) : null}
                          <Field
                            label={t("products.fields.weightGrams")}
                            optionalLabel={t("common.states.optional")}
                            className="sm:col-span-2"
                          >
                            <Input
                              value={v.weightGrams}
                              inputMode="numeric"
                              onChange={(e) => setVariant(index, { weightGrams: e.target.value })}
                              className="text-right tabular"
                              disabled={isWeb}
                            />
                          </Field>
                          <Field label={t("products.fields.reorderPoint")} error={err(`${path}.reorderPoint`)}>
                            <Input
                              value={v.reorderPoint}
                              inputMode="numeric"
                              onChange={(e) => setVariant(index, { reorderPoint: e.target.value })}
                              className="text-right tabular"
                            />
                          </Field>
                          <Field label={t("products.fields.targetStock")} error={err(`${path}.targetStock`)}>
                            <Input
                              value={v.targetStock}
                              inputMode="numeric"
                              onChange={(e) => setVariant(index, { targetStock: e.target.value })}
                              className="text-right tabular"
                            />
                          </Field>
                          <Field label={t("products.fields.mainSupplier")} className="sm:col-span-2">
                            <Select
                              value={v.mainSupplierId}
                              onValueChange={(id) => setVariant(index, { mainSupplierId: id })}
                              options={supplierOptions}
                            />
                          </Field>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {isWeb ? null : (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="self-start"
                    onClick={() => form.setFieldValue("variants", [...values.variants, emptyVariant(values.axes.length)])}
                  >
                    <Plus />
                    {t("products.addVariant")}
                  </Button>
                )}
              </FieldGroup>
            </div>
          );
        }}
      </form.Subscribe>
    </form>
  );
}
